import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useBlogPosts } from '../../hooks/useBlogPosts';
import { Calendar, User, Tag, ArrowLeft } from 'lucide-react';
import { cn } from '../../lib/utils';

const BlogHome = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { fetchPosts, fetchCategories, posts, categories, loading, error } = useBlogPosts();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  // Load categories on mount (only once)
  useEffect(() => {
    const loadCategories = async () => {
      if (!categoriesLoaded) {
        await fetchCategories();
        setCategoriesLoaded(true);
      }
    };
    loadCategories();
  }, []); // Run only once on mount

  // Load posts on mount and when selectedCategory changes
  useEffect(() => {
    const loadPosts = async () => {
      try {
        const filters = {
          published: true, // Only show published posts
          status: 'published'
        };

        if (selectedCategory) {
          filters.category = selectedCategory;
        }

        const result = await fetchPosts(filters);
        
        // Update pagination state
        if (result) {
          setHasMore(result.hasMore || false);
          setLastDoc(result.lastDoc || null);
        }
      } catch (err) {
        console.error('Error loading blog data:', err);
        // Error is handled by the hook
      }
    };

    // Reset pagination when category changes
    setHasMore(true);
    setLastDoc(null);
    loadPosts();
  }, [selectedCategory]); // Only depend on selectedCategory

  const filteredPosts = posts.filter(post => {
    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = (
        post.title?.toLowerCase().includes(searchLower) ||
        post.excerpt?.toLowerCase().includes(searchLower) ||
        post.categories?.some(cat => cat.toLowerCase().includes(searchLower)) ||
        post.tags?.some(tag => tag.toLowerCase().includes(searchLower))
      );
      if (!matchesSearch) return false;
    }

    // Filter by selected category
    if (selectedCategory) {
      if (!post.categories?.includes(selectedCategory)) return false;
    }

    return true;
  });

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (searchTerm) {
      params.set('search', searchTerm);
    } else {
      params.delete('search');
    }
    setSearchParams(params);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    const params = new URLSearchParams(searchParams);
    if (category) {
      params.set('category', category);
    } else {
      params.delete('category');
    }
    setSearchParams(params);
  };

  const handleLoadMore = async () => {
    if (!hasMore || loadingMore || !lastDoc) return;

    setLoadingMore(true);
    try {
      const filters = {
        published: true,
        status: 'published',
        startAfter: lastDoc
      };

      if (selectedCategory) {
        filters.category = selectedCategory;
      }

      const result = await fetchPosts(filters);
      
      // Update pagination state
      if (result) {
        setHasMore(result.hasMore || false);
        setLastDoc(result.lastDoc || null);
        // Note: fetchPosts automatically appends new posts to the posts state when startAfter is provided
      }
    } catch (err) {
      console.error('Error loading more posts:', err);
    } finally {
      setLoadingMore(false);
    }
  };


  // Split categories for pills and dropdown (always call hooks at top level)
  const VISIBLE_COUNT = 6;
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const safeCategories = categories || []; // Fallback to empty array if categories is undefined
  const visibleCategories = safeCategories.slice(0, VISIBLE_COUNT);
  const moreCategories = safeCategories.slice(VISIBLE_COUNT);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  if (loading && posts.length === 0 && !categoriesLoaded) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-12 w-64 bg-muted rounded mb-8"></div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-64 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      <Helmet>
        <title>Blog | Coooly — Insights for Dental Hygienists & Therapists</title>
        <meta name="description" content="Tips, guides and news for self-employed dental hygienists and therapists in the UK — invoicing, tax, career and more." />
        <link rel="canonical" href="https://coooly.co.uk/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Blog | Coooly — Insights for Dental Hygienists & Therapists" />
        <meta property="og:description" content="Tips, guides and news for self-employed dental hygienists and therapists in the UK — invoicing, tax, career and more." />
        <meta property="og:url" content="https://coooly.co.uk/blog" />
        <meta property="og:image" content="https://coooly.co.uk/logo512.png" />
        <meta property="og:site_name" content="Coooly" />
      </Helmet>
      {/* Navigation Bar */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Link 
              to="/"
              className="inline-flex items-center text-gray-600 hover:text-purple-600 transition-colors font-medium"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>
            <div className="text-sm text-gray-500">
              Professional insights & resources
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-blue-600 text-white">
        <div className="container mx-auto px-4 py-12 md:py-16">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 bg-white/20 rounded-full text-white/90 text-sm font-medium mb-6">
              📚 Professional insights & resources
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-8 bg-gradient-to-r from-white to-purple-100 bg-clip-text text-transparent leading-normal py-2">
              Dental Professional Blog
            </h1>
            <p className="text-lg md:text-xl lg:text-2xl text-purple-100 max-w-3xl mx-auto leading-relaxed">
              Expert advice, industry insights, and practical tips for dental hygienists, therapists, nurses, and locum professionals
            </p>
          </div>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="container mx-auto px-4 -mt-6 relative z-10">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 mb-12">
          <div className="flex flex-col lg:flex-row gap-6 items-center">
            {/* Search */}
            <form onSubmit={handleSearch} className="flex gap-3 flex-1 w-full lg:max-w-md">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-700 bg-gray-50 focus:bg-white transition-all"
                />
              </div>
              <button
                type="submit"
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:from-purple-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 font-medium transition-all shadow-lg hover:shadow-xl"
              >
                Search
              </button>
            </form>

            {/* Category Filter - only show if categories are loaded */}
            {categoriesLoaded && (
              <div className="flex flex-wrap gap-2 items-center relative">
                <button
                  onClick={() => handleCategoryChange('')}
                  className={cn(
                    "px-4 py-2 text-sm rounded-full font-medium transition-all transform hover:scale-105",
                    !selectedCategory
                      ? "bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-200"
                  )}
                >
                  All Categories
                </button>
                {visibleCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleCategoryChange(category.name)}
                    className={cn(
                      "px-4 py-2 text-sm rounded-full font-medium transition-all transform hover:scale-105",
                      selectedCategory === category.name
                        ? "bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg"
                        : "bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-200"
                    )}
                  >
                    {category.name}
                  </button>
                ))}
                {moreCategories.length > 0 && (
                  <div className="relative" ref={dropdownRef}>
                    <button
                      onClick={() => setShowDropdown((v) => !v)}
                      className={cn(
                        "px-4 py-2 text-sm rounded-full font-medium transition-all transform hover:scale-105 flex items-center gap-1",
                        moreCategories.some(cat => cat.name === selectedCategory)
                          ? "bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg"
                          : "bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-200"
                      )}
                    >
                      More
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                    </button>
                    {showDropdown && (
                      <div className="absolute left-0 mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-lg z-50">
                        {moreCategories.map((category) => (
                          <button
                            key={category.id}
                            onClick={() => {
                              handleCategoryChange(category.name);
                              setShowDropdown(false);
                            }}
                            className={cn(
                              "block w-full text-left px-4 py-2 text-sm rounded-xl font-medium transition-all hover:bg-purple-50",
                              selectedCategory === category.name
                                ? "bg-gradient-to-r from-purple-600 to-blue-600 text-white"
                                : "text-gray-700"
                            )}
                          >
                            {category.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="container mx-auto px-4 pb-16">
        {/* Error State */}
        {error && (
          <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6 mb-8">
            <div className="text-red-800 font-semibold mb-2">Error loading blog content</div>
            <p className="text-red-600 mb-4">{error}</p>
            <div className="text-sm text-red-600 mb-4">
              This might be a temporary issue. Please try:
              <ul className="list-disc ml-5 mt-2">
                <li>Refreshing the page</li>
                <li>Checking your internet connection</li>
                <li>Trying again in a few minutes</li>
              </ul>
            </div>
            <button
              onClick={() => {
                window.location.reload();
              }}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Blog Posts Grid */}
        {filteredPosts.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-12 text-center">
            <div className="text-gray-500 mb-4 text-lg">
              {searchTerm || selectedCategory ? 'No posts match your criteria' : 'No blog posts published yet'}
            </div>
            {(searchTerm || selectedCategory) && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  handleCategoryChange('');
                }}
                className="text-purple-600 hover:text-purple-800 font-medium hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {filteredPosts.map((post) => (
              <article key={post.id} className="group">
                <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 overflow-hidden h-full">
                  {/* Featured Image */}
                  {post.featuredImage && (
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={post.featuredImage}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                  )}

                  <div className="p-6">
                    {/* Categories */}
                    {post.categories && post.categories.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {post.categories.slice(0, 2).map((category, index) => (
                          <span
                            key={index}
                            className="inline-block px-3 py-1 bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 text-xs font-semibold rounded-full"
                          >
                            {category}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Title */}
                    <h2 className="text-xl font-bold text-gray-800 mb-3 line-clamp-2 group-hover:text-purple-600 transition-colors">
                      <Link to={`/blog/${post.slug}`}>
                        {post.title}
                      </Link>
                    </h2>

                    {/* Meta Info */}
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                      <div className="flex items-center gap-1">
                        <User className="h-4 w-4" />
                        <span className="font-medium">{post.author?.name}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        <span>
                          {new Date(post.publishedAt || post.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                    </div>

                    {/* Excerpt */}
                    {post.excerpt && (
                      <p className="text-gray-600 line-clamp-3 mb-4 leading-relaxed">
                        {post.excerpt}
                      </p>
                    )}

                    {/* Tags */}
                    {post.tags && post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {post.tags.slice(0, 3).map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-gray-100 text-gray-600"
                          >
                            <Tag className="h-3 w-3 mr-1" />
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Read More Link */}
                    <Link
                      to={`/blog/${post.slug}`}
                      className="inline-flex items-center text-purple-600 hover:text-purple-800 font-semibold text-sm group-hover:underline transition-colors"
                    >
                      Read article →
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* Load More */}
        {filteredPosts.length > 0 && hasMore && (
          <div className="text-center mt-16">
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:from-purple-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 font-semibold text-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
            >
              {loadingMore ? 'Loading...' : 'Load More Articles'}
            </button>
          </div>
        )}

        {/* End of Results Message */}
        {filteredPosts.length > 0 && !hasMore && (
          <div className="text-center mt-16">
            <div className="text-gray-500 font-medium">
              You've reached the end of our blog posts! 
            </div>
            <div className="text-sm text-gray-400 mt-2">
              Check back soon for more professional insights and tips.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogHome;
